import graphql__ec9805304df09357be65 from "./__generated__/EditBankTransactionRuleDialogAddMutation.graphql.ts";
import graphql__5d683ac02e7d7c8bf5a3 from "./__generated__/EditBankTransactionRuleDialogChangeMutation.graphql.ts";import * as React from "react";
import {graphql} from "react-relay";
import {useRelayEnvironment} from "react-relay/hooks";
import {useIntl} from "react-intl";
import * as yup from "yup";
import FormDialog from "./FormDialog";
import Section from "../form/Section";
import Row from "../form/Row";
import Label from "../form/Label";
import Field from "../form/Field";
import {commitMutationWithErrorHandling} from "../../helper/commitMutationWithErrorHandling";
import {useErrorContext} from "../../contexts/ErrorContext";
import type {EditBankTransactionRuleDialogAddMutation} from "./__generated__/EditBankTransactionRuleDialogAddMutation.graphql";
import type {EditBankTransactionRuleDialogChangeMutation} from "./__generated__/EditBankTransactionRuleDialogChangeMutation.graphql";

type Props = {
    // Without a rule, the dialog adds one behind all other rules
    rule?: {
        id: string
        field: string
        containedText: string
        direction: string | null
        transactionGroup: string
    }
    onClose: (changed: boolean) => void
}

const fields = ['COUNTERPARTY_NAME', 'PURPOSE'];
const directions = ['', 'IN', 'OUT'];
const groups = ['CUSTOMER_PAYMENTS', 'SUPPLIERS', 'SALARIES', 'TAX_OFFICE', 'FINANCING', 'TRANSFER', 'OTHER'];

const addMutation = graphql__ec9805304df09357be65;

const changeMutation = graphql__5d683ac02e7d7c8bf5a3;

/**
 * Adds a rule or changes what it matches and the group it assigns. A rule applies from the next
 * upload on.
 */
const EditBankTransactionRuleDialog: React.FC<Props> = ({rule, onClose}) => {
    const intl = useIntl();
    const relayEnvironment = useRelayEnvironment();
    const {addError} = useErrorContext();

    const handleSubmission = (values) => {
        const input = {
            field: values.field,
            containedText: values.containedText.trim(),
            direction: values.direction === '' ? null : values.direction,
            transactionGroup: values.transactionGroup,
        };
        const options = {
            variables: {input: rule ? {id: rule.id, ...input} : input},
            onCompleted: (_, errors) => {
                if (errors && errors.length > 0) {
                    console.error(errors);
                }
                onClose(true);
            },
            onError: (error) => {
                console.error(error);
                onClose(false);
            },
        };
        const customErrorMessage = intl.formatMessage({id: rule ? 'errors.failedToChangeBankTransactionRule' : 'errors.failedToAddBankTransactionRule'});
        if (rule) {
            commitMutationWithErrorHandling<EditBankTransactionRuleDialogChangeMutation>(relayEnvironment, {mutation: changeMutation, ...options}, addError, {customErrorMessage});
        } else {
            commitMutationWithErrorHandling<EditBankTransactionRuleDialogAddMutation>(relayEnvironment, {mutation: addMutation, ...options}, addError, {customErrorMessage});
        }
    };

    const validationSchema = yup.object().shape({
        containedText: yup.string()
            .trim()
            .max(255, intl.formatMessage({id: 'validation.field.nameTooLong'}))
            .required(intl.formatMessage({id: 'validation.field.required'})),
    });

    return (
        <FormDialog handleCloseDialog={() => onClose(false)}
                    handleSubmission={handleSubmission}
                    initialValues={{
                        field: rule?.field ?? 'COUNTERPARTY_NAME',
                        containedText: rule?.containedText ?? '',
                        direction: rule?.direction ?? '',
                        transactionGroup: rule?.transactionGroup ?? 'SUPPLIERS',
                    }}
                    validationSchema={validationSchema}
                    headline={intl.formatMessage({id: rule ? 'dialog.EditBankTransactionRuleDialog.editHeadline' : 'dialog.EditBankTransactionRuleDialog.addHeadline'})}
                    submitLabel={intl.formatMessage({id: 'button.save'})}>
            <Section>
                <Row>
                    <Label>{intl.formatMessage({id: 'dialog.EditBankTransactionRuleDialog.fieldLabel'})}</Label>
                    <Field name="field" as="select">
                        {fields.map(field => (
                            <option key={field} value={field}>{intl.formatMessage({id: 'bankTransactionRuleField.' + field})}</option>
                        ))}
                    </Field>
                </Row>
                <Row>
                    <Label>{intl.formatMessage({id: 'dialog.EditBankTransactionRuleDialog.containedTextLabel'})}</Label>
                    <Field name="containedText" type="text" autoFocus={true} data-1p-ignore="true"/>
                </Row>
                <Row>
                    <Label>{intl.formatMessage({id: 'dialog.EditBankTransactionRuleDialog.directionLabel'})}</Label>
                    <Field name="direction" as="select">
                        {directions.map(direction => (
                            <option key={direction} value={direction}>{intl.formatMessage({id: 'bankTransactionDirection.' + (direction === '' ? 'BOTH' : direction)})}</option>
                        ))}
                    </Field>
                </Row>
                <Row>
                    <Label>{intl.formatMessage({id: 'dialog.EditBankTransactionRuleDialog.groupLabel'})}</Label>
                    <Field name="transactionGroup" as="select">
                        {groups.map(group => (
                            <option key={group} value={group}>{intl.formatMessage({id: 'bankTransactionGroup.' + group})}</option>
                        ))}
                    </Field>
                </Row>
                <p className="text-sm text-gray-500">
                    {intl.formatMessage({id: 'dialog.EditBankTransactionRuleDialog.description'})}
                </p>
            </Section>
        </FormDialog>
    );
};

export default EditBankTransactionRuleDialog;
